import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { getSearchDictionary } from '@theme/search-dictionary';
import { correctSearchQuery } from '@theme/search-query-corrector';
import { matchSearchIntent } from '@theme/search-intent-dictionary';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#tryCorrectedEmptySearch();
  }

  /**
   * If the native Shopify search on /search returned zero results, attempts:
   * 1. A single corrected re-query using the brand dictionary/corrector, same as
   *    the predictive search modal, so both surfaces stay consistent.
   * 2. Only if that also leaves zero results, a single related-products re-query
   *    against the curated intent table (see @theme/search-intent-dictionary).
   * Never runs when results already exist, never more than one extra request per
   * stage, and degrades silently on any failure.
   */
  async #tryCorrectedEmptySearch() {
    const resultsCount = Number(this.dataset.resultsCount ?? '-1');
    const searchTerm = this.dataset.searchTerm ?? '';

    if (resultsCount !== 0 || !searchTerm) return;

    try {
      let stillEmpty = true;

      const dictionary = await getSearchDictionary();
      const correction = correctSearchQuery(searchTerm, dictionary);

      if (correction) {
        const correctedUrl = new URL(window.location.href);
        correctedUrl.searchParams.set('q', correction.correctedQuery);

        await sectionRenderer.renderSection(this.sectionId, { url: correctedUrl, cache: false });

        stillEmpty = Number(this.dataset.resultsCount ?? '0') === 0;

        if (!stillEmpty) {
          history.replaceState(history.state, '', correctedUrl);

          // Scoped to the visible /search page input specifically — a plain
          // input[name="q"] query would also match the (usually hidden) predictive
          // search modal input, which is rendered globally in the header and tends
          // to come first in document order.
          const searchInput = document.querySelector('.search-page-input');
          if (searchInput instanceof HTMLInputElement) {
            searchInput.value = correction.correctedQuery;
          }
        }
      }

      if (stillEmpty) {
        const intent = matchSearchIntent(searchTerm);
        if (!intent) return;

        const intentUrl = new URL(window.location.href);
        intentUrl.searchParams.set('q', intent.query);

        // URL intentionally NOT updated here — it would expose the internal
        // tag:/title: query syntax in a bookmarkable/shareable link. Only the
        // results grid changes; a refresh or back-navigation returns to the
        // original (empty) search, which is an acceptable trade-off for this
        // best-effort related-products layer.
        await sectionRenderer.renderSection(this.sectionId, { url: intentUrl, cache: false });
      }
    } catch {
      // Silent degradation — the native "no results" state remains untouched.
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
